import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/api-client';

export interface TenantConfig {
  id: string;
  name: string;
  code: string;
  gamEnabled: boolean;
  gamNetworkId: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Squares ad placement keys - same as Pickem for list views
 * BODY: Ads between contest cards (Details, My Games tabs)
 * STICKY_FOOTER: 320x50 sticky footer
 * SQUARES_OVER_LOGO: 1x1 tracking pixel over the sponsor logo
 * SQUARES_POST_ENTRY: banner on the square submitted confirmation modal
 */
export type GoogleAdsPlacementKey =
  | 'BODY'
  | 'STICKY_FOOTER'
  | 'SQUARES_OVER_LOGO'
  | 'SQUARES_POST_ENTRY';

export type AdSize = [number, number];

export interface ResponsiveSizeMapping {
  viewport: [number, number];
  sizes: AdSize[] | [];
}

export interface GoogleAdsPlacement {
  placementKey: GoogleAdsPlacementKey;
  enabled: boolean;
  adUnitPath?: string | null;
  sizes?: AdSize[] | null;
  sizeMappings?: ResponsiveSizeMapping[] | null;
  divIdPrefix?: string | null;
  targeting?: Record<string, string> | null;
}

export type GoogleAdsPlacements = Record<GoogleAdsPlacementKey, GoogleAdsPlacement>;

export const useGetTenantGoogleAdsPlacements = (slug: string) => {
  return useQuery({
    queryKey: ['tenant', slug, 'google-ads-placements'],
    queryFn: async (): Promise<GoogleAdsPlacement[]> => {
      const response = (await apiClient.get(`/api/tenants/${slug}/google-ads-placements`)) as {
        placements?: GoogleAdsPlacement[];
      };
      return response.placements || [];
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
    retry: 3,
  });
};

/**
 * Hook to fetch tenant configuration by slug
 */
export const useGetTenant = (slug: string) => {
  return useQuery({
    queryKey: ['tenant', slug],
    queryFn: async (): Promise<TenantConfig> => {
      const response = (await apiClient.get(`/api/tenants/${slug}`)) as {
        tenant?: Record<string, unknown>;
      };
      const t = response.tenant;
      if (!t) throw new Error('Tenant not found');
      return {
        id: String(t.id),
        name: String(
          (t as { nameEn?: string }).nameEn || (t as { title?: string }).title || t.code || ''
        ),
        code: String(t.code || ''),
        gamEnabled: Boolean((t as { gamEnabled?: boolean }).gamEnabled),
        gamNetworkId: (t as { gamNetworkId?: string | null }).gamNetworkId ?? null,
        createdAt: t.createdAt as string | undefined,
        updatedAt: t.updatedAt as string | undefined,
      };
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
    retry: 3,
  });
};

export const getGAMConfigFromTenant = (tenant: TenantConfig | undefined) => {
  if (!tenant || !tenant.gamEnabled) {
    return null;
  }
  return {
    networkId: tenant.gamNetworkId || '',
    enabled: tenant.gamEnabled,
  };
};
